// @flow
import * as React from 'react';
import _ from 'lodash';
import Popover from '../Popover';
import {
  CategoryQueryVAB,
  CategoryWiseDetail,
  FacetedItemInfo,
  GetFacetsMsg,
  GetFacetsQuery,
  GetFacetsRes
} from '../autogenerated/ZoomBridge';
import ErrorHandler from '../error/ErrorHandler';
import { getLabelWithoutCount } from '../util/FacetUtil';
import StringUtil from '../util/ArrayUtil';
import Store from '../store/Store';
import { GLOBAL_SEARCH_INFO, SHOW_TREE_VIEW } from '../store/Constants';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';

const debug = require('../debug/debug')('wc:common:TreeMultiSelect');

type Props = {
  category: CategoryWiseDetail,
  categoryList: CategoryWiseDetail[],
  onCategoryChangeEvent: Function,
  resetExpandedNodes: boolean
};

type State = {
  filter: string
};

const _store = {
  expandedNodes: {}
};

export default class TreeMultiSelect extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      filter: ''
    };
    if (props.resetExpandedNodes) {
      _store.expandedNodes = {};
    } else {
      this.init();
    }
  }

  static defaultProps = {
    resetExpandedNodes: false
  };

  /**
   * Sets the `isExpanded` property on parent nodes based on values in
   * the `_store` module variable. This is so the popup looks the same
   * as the last time the user viewed it in the current session.
   */
  init = () => {
    const { category } = this.props;
    if (!category) {
      return;
    }

    const data = category.getTreeData();
    if (!(data && data.length)) {
      return;
    }

    const children = data[0].getChildren();
    if (!(children && children.length)) {
      return;
    }

    children.forEach((child) => {
      const key = this.getNodeKey(child);
      const isExpanded = Boolean(_store.expandedNodes[key]);
      child.isExpanded = isExpanded;
      _store.expandedNodes[key] = isExpanded;
    });
  };

  getNodeKey = (node: FacetedItemInfo) => {
    return `${node.getId()}_${node.getLabel()}`;
  };

  getParentNode(input: FacetedItemInfo) {
    let resItem = null;
    const parentId = input.getParentId();
    if (parentId > 0) {
      const options = this.props.category.getTreeData()[0].getChildren();
      resItem = options.find(function(item) {
        if (item.getId() == parentId) return true;
      });
    }
    return resItem;
  }

  onGroupClick(group: FacetedItemInfo) {
    this.onCategoryChange(group, true);
  }

  onCategoryChange(option: FacetedItemInfo, isGroup: boolean = false) {
    option.setSelected(!option.isSelected());
    const parentNode = isGroup ? null : this.getParentNode(option);
    this.props.onCategoryChangeEvent(this.props.category.getCategoryId(), option.isSelected(), option, parentNode);
    this.props.category.isOpened = false;
    window.render();
  }

  onChangeFilterTextDelayed = _.debounce((value) => {
    this.onChangeFilterText(value);
  }, 300);

  onChangeFilterText(val: string) {
    this.issueFacetsQuery(val);
  }

  issueFacetsQuery(matchString: string) {
    const { categoryList } = this.props;
    const facetQuery = new GetFacetsQuery();
    const msg = new GetFacetsMsg();
    msg.setUseCurrentFuidSet(true);
    facetQuery.message = msg;
    msg._useCategoryListForResponse = true;
    const cv = new CategoryQueryVAB();
    cv._matchString = matchString;
    cv._kidsQuery = true;

    // if (this.props.category.getCategoryId() == 'metadata_key') {
    //   const propLabel = _.find(this.props.category.getName2count(), option => option.getParentId() <= 0).getLabel();
    //   cv._parentIdName = propLabel.substr(0, propLabel.lastIndexOf(" ("));
    // }

    cv._categoryId = this.props.category.getCategoryId();

    msg._categoryList = [cv];
    debug('[issueFacetsQuery()] running query: ', facetQuery);
    facetQuery.run().then(
      (resp: GetFacetsRes) => {
        debug('[issueFacetsQuery()] response: ', resp);
        const categoryIndex = _.findIndex(
          categoryList,
          (item) => item.getCategoryId() == this.props.category.getCategoryId()
        );
        const __categoryList = resp.getCategoryList();
        if (__categoryList[0] && __categoryList[0].getTreeData()) {
          const treeData = __categoryList[0].getTreeData();
          debug('[issueFacetsQuery()] updating treeData for categoryList item: ', {
            index: categoryIndex,
            categoryList,
            treeData
          });

          categoryList[categoryIndex].setTreeData(treeData);
          this.setState({ filter: matchString });
        }
      },
      (error) => {
        ErrorHandler.handleError(error);
      }
    );
  }

  onChange = (e: KeyboardEvent) => {
    const { category } = this.props;
    // getting the input value via event.target.value (there was ref="filterInput", which shouldn't be used for getting values)
    const { value } = e.target;
    debug('[onChange()] setting matchString for category: ', { matchString: value, category });
    category.setMatchString(value); // on the client change the value instantly without debouncing
    window.render();
    this.onChangeFilterTextDelayed(value); // send a request only after a certain delay
  };

  renderOptionValue(option: FacetedItemInfo) {
    const globalSearchInfo: Object = Store.get(GLOBAL_SEARCH_INFO);
    const showTreeView: boolean = Store.get(SHOW_TREE_VIEW);
    let label;
    if (globalSearchInfo.active || !showTreeView) {
      label = StringUtil.isNotEmpty(option.getDisplayLabel()) ? option.getDisplayLabel() : option.getLabel();
      label += ' (' + option.getCount() + ')';
    } else {
      label = StringUtil.isNotEmpty(option.getDisplayLabel()) ? option.getDisplayLabel() : getLabelWithoutCount(option);
    }
    return <div style={{ left: '50px', position: 'absolute', color: 'var(--primary-font-color)' }}>{label}</div>;
  }

  renderOption(option: FacetedItemInfo, index: number) {
    return (
      <div key={option.getLabel() + '-' + index} onClick={() => this.onCategoryChange(option)} style={styles.option}>
        <img
          src={
            option.isSelected()
              ? ThemeSetup.getImageURL('icon_radio_checked.svg')
              : ThemeSetup.getImageURL('checkmark_unchecked.svg')
          }
          style={{
            width: '16px',
            height: '16px',
            cursor: 'pointer',
            float: 'left',
            position: 'absolute',
            top: '10px',
            left: '27px'
          }}
        />
        {this.renderOptionValue(option)}
      </div>
    );
  }

  renderGroup(group: FacetedItemInfo, index: number) {
    if (group.getId() === -555) {
      return null;
    }

    const options: FacetedItemInfo[] = group.getChildren();
    const isExpanded = group.isExpanded || Boolean(this.state.filter.trim());

    return (
      <div key={group.getLabel() + '-' + index}>
        <div style={styles.group}>
          <div
            onClick={() => {
              const isExpanded = !group.isExpanded;
              group.isExpanded = isExpanded;
              _store.expandedNodes[this.getNodeKey(group)] = isExpanded;
              this.forceUpdate();
            }}
            style={{
              ...styles.collapseButton,
              backgroundImage: isExpanded ? 'url(images/arrow_up.svg)' : 'url(images/arrow_down.svg)'
            }}
          />
          <img
            src={
              group.isSelected()
                ? ThemeSetup.getImageURL('icon_radio_checked.svg')
                : ThemeSetup.getImageURL('checkmark_unchecked.svg')
            }
            onClick={() => this.onGroupClick(group)}
            style={{
              width: '16px',
              cursor: 'pointer',
              height: '16px',
              position: 'absolute',
              top: '10px',
              left: '27px'
            }}
          />
          <span
            onClick={() => this.onGroupClick(group)}
            style={{ position: 'absolute', left: '50px', cursor: 'pointer', color: 'var(--primary-font-color)' }}>
            {getLabelWithoutCount(group)}
          </span>
        </div>
        <div style={{ paddingLeft: '20px' }}>
          {isExpanded ? _.map(options, (option, index) => this.renderOption(option, index)) : null}
        </div>
      </div>
    );
  }

  render() {
    const { category } = this.props;
    const groups: FacetedItemInfo[] = category.getTreeData()[0].getChildren();
    const color = category.isHasAnySelectedItem() ? 'var(--primary-style-color)' : 'var(--primary-font-color)';
    const totalCount = groups.length + groups.reduce((i, group) => i + group.getChildren().length, 0);
    const value = category.getMatchString() || '';
    const iconUp = category.isHasAnySelectedItem()
      ? ThemeSetup.getBackgroundURL('arrow_up_red.svg')
      : 'url(images/arrow_up.svg)';
    const iconDown = category.isHasAnySelectedItem()
      ? ThemeSetup.getBackgroundURL('arrow_down_red.svg')
      : 'url(images/arrow_down.svg)';

    return (
      <div style={{ float: 'left', marginRight: '10px', marginBottom: '10px' }} ref="select" name="TreeMultiSelect">
        <div
          style={{
            ...styles.select,
            color,
            backgroundColor: category.isOpened ? 'var(--dropdown-bg-selected)' : 'var(--primary-header-bg)',
            backgroundImage: category.isOpened ? iconUp : iconDown
          }}
          onClick={() => {
            category.isOpened = true;
            window.render();
          }}>
          {category.getLabel()}
        </div>
        {category.isOpened ? (
          <Popover
            onClose={() => {
              category.isOpened = false;
              window.render();
            }}
            left={this.refs.select.getBoundingClientRect().left}
            top={this.refs.select.getBoundingClientRect().bottom}>
            <div style={styles.options}>
              {totalCount >= 6 || value ? (
                <div style={{ borderBottom: '1px solid #E7E7E7' }}>
                  <input
                    ref="filterInput"
                    onChange={this.onChange}
                    value={value}
                    placeholder={t('FILTER_LABEL')}
                    style={styles.filter}
                    type="text"
                  />
                </div>
              ) : null}
              {_.map(groups, (option, index) => this.renderGroup(option, index))}
            </div>
          </Popover>
        ) : null}
      </div>
    );
  }
}

const styles = {
  select: {
    padding: '6px 30px 6px 10px',
    minWidth: '130px',
    fontSize: '14px',
    cursor: 'pointer',
    height: '33px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'calc(100% - 10px) 15px',
    borderRadius: '3px',
    boxShadow: '0 1px 4px -2px rgba(0,0,0,0.10)'
  },
  option: {
    cursor: 'pointer',
    padding: '10px',
    fontSize: '12px',
    position: 'relative',
    height: '35px'
  },
  filter: {
    outline: 'none',
    border: 'none',
    fontSize: '14px',
    width: '100%',
    padding: '10px 10px 10px 12px',
    lineHeight: '18px',
    color: 'var(--primary-font-color)',
    fontFamily: 'Montserrat'
  },
  options: {
    boxShadow: '0 15px 30px -10px rgba(0,0,0,0.20)',
    position: 'fixed',
    zIndex: '2',
    background: 'var(--primary-header-bg)',
    width: '400px',
    maxHeight: '50vh',
    overflowY: 'auto'
  },
  group: {
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '10px 50%',
    padding: '10px 10px 10px 25px',
    fontSize: '12px',
    position: 'relative',
    height: '35px'
  },
  collapseButton: {
    left: '10px',
    top: '7px',
    position: 'absolute',
    width: '10px',
    height: '20px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    cursor: 'pointer'
  }
};
